import { useCallback, useLayoutEffect, useMemo, useRef, type ReactNode } from 'react'

import Box from '@mui/material/Box'

import Popover from '@mui/material/Popover'

import Typography from '@mui/material/Typography'

import Button from '@mui/material/Button'

import CircularProgress from '@mui/material/CircularProgress'

import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined'

import { env } from '@/config/env'

import { flattenNotificationPages, useNotifications } from '../api/notification-queries'

import { useMarkAllRead } from '../api/notification-mutations'

import { NotificationItem } from './NotificationItem'

interface Props {
  readonly anchorEl: HTMLElement | null
  readonly open: boolean
  readonly onClose: () => void
}

/** When within this many px of the bottom of the list, fetch the next page (lazy load). */
const FETCH_NEXT_THRESHOLD_PX = 120

export function NotificationPanel({ anchorEl, open, onClose }: Props) {
  const listRef = useRef<HTMLDivElement>(null)

  const hasNextRef = useRef(false)

  const fetchingNextRef = useRef(false)

  const fetchNextRef = useRef<() => Promise<unknown>>(async () => {})

  const {
    data,

    isPending,

    isFetchingNextPage,

    hasNextPage,

    fetchNextPage,
  } = useNotifications()

  const { mutate: markAllRead, isPending: isMarkingAll } = useMarkAllRead()

  const notifications = useMemo(
    () => (data?.pages?.length ? flattenNotificationPages(data.pages) : []),
    [data?.pages],
  )

  const hasUnread = notifications.some((n) => !n.isRead)

  hasNextRef.current = Boolean(hasNextPage)

  fetchingNextRef.current = Boolean(isFetchingNextPage)

  fetchNextRef.current = fetchNextPage

  const tryFetchNextPage = useCallback(() => {
    const el = listRef.current

    if (!el) return

    if (!hasNextRef.current || fetchingNextRef.current) return

    const distance = el.scrollHeight - el.scrollTop - el.clientHeight

    if (distance > FETCH_NEXT_THRESHOLD_PX) return

    void fetchNextRef.current()
  }, [])

  useLayoutEffect(() => {
    if (!open || isPending || notifications.length === 0) return

    if (!hasNextPage) return

    const id = requestAnimationFrame(() => tryFetchNextPage())

    return () => cancelAnimationFrame(id)
  }, [open, isPending, hasNextPage, notifications.length, data?.pages?.length, tryFetchNextPage])

  const showEndOfList = !isPending && notifications.length > 0 && !hasNextPage

  let listBody: ReactNode
  if (isPending) {
    listBody = (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: '32px',
        }}
      >
        <CircularProgress size={20} sx={{ color: '#008cd0' }} />
      </Box>
    )
  } else if (notifications.length === 0) {
    listBody = (
      <Box
        data-testid="notification-empty-state"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          px: '24px',
          py: '40px',
        }}
      >
        <NotificationsNoneOutlinedIcon sx={{ fontSize: '32px', color: '#d1d5db' }} />
        <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#6b7280', textAlign: 'center' }}>
          No notifications yet
        </Typography>
        <Typography sx={{ fontSize: '12px', color: '#9ca3af', textAlign: 'center', lineHeight: '18px' }}>
          Campaign updates and important alerts will appear here.
        </Typography>
      </Box>
    )
  } else {
    listBody = (
      <>
        {notifications.map((notification) => (
          <NotificationItem key={notification.id} notification={notification} onClose={onClose} />
        ))}
        {hasNextPage && isFetchingNextPage && (
          <Box
            data-testid="notification-loading-more"
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: '12px',
              borderTop: '1px solid #f0f0f0',
            }}
          >
            <CircularProgress size={16} sx={{ color: '#008cd0' }} />
          </Box>
        )}
        {showEndOfList && (
          <Box
            data-testid="notification-list-end"
            role="status"
            aria-live="polite"
            sx={{
              bgcolor: '#ffffff',
              px: '16px',
              py: '16px',
            }}
          >
            <Typography
              component="p"
              sx={{
                fontSize: '12px',
                fontWeight: 400,
                lineHeight: '18px',
                color: '#9ca3af',
                textAlign: 'center',
                m: 0,
              }}
            >
              {`That's all your notifications from the last ${env.VITE_BELL_NOTIFICATION_HISTORY_DAYS} days.`}
            </Typography>
          </Box>
        )}
      </>
    )
  }

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      data-testid="notification-panel"
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{
        paper: {
          sx: {
            width: '320px',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '8px',
            border: '1px solid #e1e7ef',
            boxShadow:
              '0px 10px 15px -3px rgba(0,0,0,0.1), 0px 4px 6px -4px rgba(0,0,0,0.1)',
            overflow: 'hidden',
            mt: '4px',
          },
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: '16px',
          pt: '12px',
          pb: '13px',
          flexShrink: 0,
          borderBottom: '1px solid #e1e7ef',
          bgcolor: '#ffffff',
        }}
      >
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: '14px',
            lineHeight: '20px',
            color: '#0f1729',
          }}
        >
          Notifications
        </Typography>
        {hasUnread && (
          <Button
            size="small"
            onClick={() => markAllRead()}
            disabled={isMarkingAll}
            data-testid="notification-mark-all-read"
            sx={{
              fontSize: '12px',
              fontWeight: 400,
              lineHeight: '16px',
              color: '#008cd0',
              textTransform: 'none',
              p: 0,
              minWidth: 'auto',
              '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
              '&.Mui-disabled': { color: '#9ca3af' },
            }}
          >
            Mark all as read
          </Button>
        )}
      </Box>

      <Box
        ref={listRef}
        data-testid="notification-list-scroll"
        onScroll={tryFetchNextPage}
        sx={{
          maxHeight: '288px',
          overflowY: 'auto',
          bgcolor: '#ffffff',
          flexShrink: 1,
          minHeight: 0,
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: '#e1e7ef',
            borderRadius: '2px',
          },
        }}
      >
        {listBody}
      </Box>
    </Popover>
  )
}
